import { AutenticationModuleRoutes, AutenticationPath } from 'app/autentication/routes';
import { AutenticationApiActions } from 'app/autentication/store/actions';
import { ConfigModuleRoutes, ConfigurationPath } from 'app/configuration/router';
import { GlobalContext } from 'context';
import { Rol } from 'domain/rol';
import { ModuleRoute } from 'module-routes';
import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ImpersonationBanner from 'shared/components/ImpersonationBanner/ImpersonationBanner';
import InitialsAvatar from 'shared/components/InitialsAvatar/InitialsAvatar';
import useDispatchState from 'shared/hooks/useDispatchState';
import ModalDialogue from '../ModalDialogue/ModalDialogue';
import ExpiryAlerts from './ExpiryAlerts/ExpiryAlerts';
import HeaderStyled from './Header.styled';

const { MenuToggleButton, UserMenuButton, UserMenu, UserSummary } = HeaderStyled;

const BRANCH_SWITCHER_ROLES = [Rol.SuperAdmin, Rol.Admin];

const Header = (): ReactElement => {
  const [{ accessUser, login }, dispatchApp] = useDispatchState(GlobalContext);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isNavMd, setIsNavMd] = useState<boolean>(true);
  const navigateRoute = useNavigate();

  const setContentHeight = (): void => {
    const rightCol = document.querySelector('.right_col') as HTMLElement;
    const body = document.body;
    const footer = document.querySelector('footer') as HTMLElement;
    const leftCol = document.querySelector('.left_col') as HTMLElement;
    const sidebarFooter = document.querySelector('.sidebar-footer') as HTMLElement;
    const navMenu = document.querySelector('.nav_menu') as HTMLElement;

    if (!rightCol || !body || !footer || !leftCol || !navMenu) return;

    rightCol.style.minHeight = `${window.innerHeight}px`;

    const bodyHeight = body.offsetHeight;
    const footerHeight = body.classList.contains('footer_fixed') ? -10 : footer.offsetHeight;
    const leftColHeight = leftCol.offsetHeight + (sidebarFooter?.offsetHeight ?? 0);
    let contentHeight = Math.max(bodyHeight, leftColHeight);

    contentHeight -= navMenu.offsetHeight + footerHeight;

    rightCol.style.minHeight = `${contentHeight}px`;
  };

  const handleMenuToggle = (): void => {
    const body = document.body;
    const sidebarMenu = document.getElementById('sidebar-menu') as HTMLElement;

    if (isNavMd) {
      sidebarMenu.querySelectorAll('li.active ul').forEach((ul) => ((ul as HTMLElement).style.display = 'none'));
      sidebarMenu.querySelectorAll('li.active').forEach((li) => {
        li.classList.add('active-sm');
        li.classList.remove('active');
      });
    } else {
      sidebarMenu.querySelectorAll('li.active-sm ul').forEach((ul) => ((ul as HTMLElement).style.display = 'block'));
      sidebarMenu.querySelectorAll('li.active-sm').forEach((li) => {
        li.classList.add('active');
        li.classList.remove('active-sm');
      });
    }

    body.classList.toggle('nav-md');
    body.classList.toggle('nav-sm');
    setIsNavMd(!isNavMd);

    setContentHeight();
  };

  useEffect(() => {
    setContentHeight();
    window.addEventListener('resize', setContentHeight);

    return () => {
      window.removeEventListener('resize', setContentHeight);
    };
  }, [isNavMd]);

  const user = accessUser?.user;
  const canSwitchBranch = Boolean(login) && BRANCH_SWITCHER_ROLES.includes(Number(login?.roleId));

  const handleLogout = async (): Promise<void> => {
    await AutenticationApiActions.logout(dispatchApp);
    setIsVisibleModal(false);
    navigateRoute(ModuleRoute.Login);
  };

  return (
    <div className="top_nav">
      <div className="nav_menu">
        <div className="nav toggle">
          <MenuToggleButton
            type="button"
            id="menu_toggle"
            aria-label="Mostrar u ocultar el menú lateral"
            aria-controls="sidebar-menu"
            onClick={handleMenuToggle}
          >
            <i className="fa fa-bars" aria-hidden="true" />
          </MenuToggleButton>
        </div>
        <nav className="nav navbar-nav">
          <ul className="navbar-right">
            <li className="nav-item dropdown open" style={{ paddingLeft: 15 }}>
              <UserMenuButton
                type="button"
                className="user-profile dropdown-toggle"
                id="navbarDropdown"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                aria-label={`Menú de usuario: ${user?.firstName ?? ''}`}
              >
                <InitialsAvatar firstName={user?.firstName} lastName={user?.lastName} />
                {user?.firstName}
              </UserMenuButton>
              <UserMenu className="dropdown-menu dropdown-usermenu pull-right" aria-labelledby="navbarDropdown">
                <UserSummary>
                  <div className="font-weight-bold text-dark">{`${user?.firstName ?? ''} ${user?.lastName ?? ''}`}</div>
                  <div className="text-muted">DNI {user?.dni}</div>
                </UserSummary>
                <div className="dropdown-divider" />

                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => navigateRoute(ConfigModuleRoutes.get(ConfigurationPath.Profile) as string)}
                >
                  <i className="fa fa-fw fa-user mr-2" aria-hidden="true" />
                  Mi perfil
                </button>

                {canSwitchBranch && (
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => navigateRoute(AutenticationModuleRoutes.get(AutenticationPath.BranchOfficeList) as string)}
                  >
                    <i className="fa fa-fw fa-institution mr-2" aria-hidden="true" />
                    Cambiar sucursal
                  </button>
                )}

                <div className="dropdown-divider" />
                <button type="button" className="dropdown-item" onClick={() => setIsVisibleModal(true)}>
                  <i className="fa fa-fw fa-sign-out mr-2" aria-hidden="true" />
                  Cerrar sesión
                </button>
              </UserMenu>
            </li>
            <ExpiryAlerts />
          </ul>
        </nav>

        <ModalDialogue
          isVisible={isVisibleModal}
          title="Cerrar sesión"
          primaryButtonLabel="Cerrar sesión"
          primaryIcon="fa-sign-out"
          size="sm"
          primaryAction={handleLogout}
          secondaryButtonLabel="Cancelar"
          secondaryAction={() => setIsVisibleModal(false)}
          onClose={() => setIsVisibleModal(false)}
        >
          ¿Estás seguro de cerrar sesión?
        </ModalDialogue>
        <ImpersonationBanner />
      </div>
    </div>
  );
};

export default Header;
